import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Layout from '../components/Layout';
import FormRow from '../components/FormRow';
import RichTextEditor from '../components/RichTextEditor';
import { useAuth } from '../context/AuthContext';
import ModalShell from '../components/ModalShell';
import ExportProjectPDF from '../components/ExportProjectPDF';
import ExportSingleLogPDF from '../components/ExportSingleLogPDF';
import ExportWorklogsExcel from '../components/ExportWorklogsExcel'; 
import ImportWorklogsExcel from '../components/ImportWorklogsExcel';
import { FaFileExcel, FaFileAlt, FaPencilAlt, FaFilePdf, FaFileUpload } from 'react-icons/fa';
import {
  listProjectsApi,
  listProjectMembersApi,
  listProjectLogsApi,
  createProjectLogApi,
  exportWorklogsApi,
  updateProjectLogApi,
  updateProjectLogApiCompat,
  deleteProjectLogApiCompat,
  listProjectDraftsApi,
  upsertDraftApi,
  publishDraftApi,
  deleteDraftApi,
} from '../api/http';

/* ================= Helpers (puros) ================= */
const pad = (n) => String(n).padStart(2, '0');
const fmtDateISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const coalesce = (...vals) => vals.find(v => v != null && v !== '') ?? '';
const getStartStr = (l) => coalesce(l?.start, l?.start_time);
const getEndStr = (l) => coalesce(l?.end, l?.end_time);
const getNoteStr = (l) => coalesce(l?.progress_note, l?.note);
const getBillable = (l) => {
  const v = l?.billable;
  if (typeof v === 'boolean') return v;
  if (v == null) return false;
  return Number(v) === 1;
};
const fmtDateStr = (d) => (d ? String(d).replace('T',' ').slice(0,16) : '');

/*html*/
function htmlToPlain(textOrHtml = '') {
  const div = document.createElement('div');
  div.innerHTML = String(textOrHtml ?? '');
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}
function parseSafeDateParts(dateStr, timeStr) {
  if (!dateStr) return null;
  const s = `${dateStr}T${timeStr || '00:00'}:00`;
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
function parseSafeDateAny(v) {
  if (!v) return null;
  const t = typeof v === 'string' ? v.replace(' ', 'T') : v;
  const d = new Date(t);
  return isNaN(d) ? null : d;
}
function formatDateOnlyCL(dateLike) {
  const d = typeof dateLike === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)
    ? parseSafeDateParts(dateLike)
    : parseSafeDateAny(dateLike);
  if (!d) return '';
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}
function formatCL(dt) {
  if (!dt) return '';
  return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}
function sanitizeHtml(html = '') { return String(html).replace(/<script/gi, '&lt;script'); }

function getLogDate(log) {
  if (log?.date) {
    const dateStr = String(log.date);
    const startStr = log?.start || log?.start_time;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const d = parseSafeDateParts(dateStr, startStr);
      if (d) return d;
    }
    const iso = parseSafeDateAny(dateStr);
    if (iso) {
      if (startStr && /^\d{2}:\d{2}$/.test(startStr)) {
        const y = iso.getFullYear(), m = pad(iso.getMonth() + 1), d = pad(iso.getDate());
        return parseSafeDateParts(`${y}-${m}-${d}`, startStr);
      }
      return iso;
    }
  }
  const d2 = parseSafeDateAny(log?.started_at || log?.startedAt);
  if (d2) return d2;
  return parseSafeDateAny(log?.created_at || log?.createdAt);
}
function getLogMonthKey(log) {
  if (log?.date) return String(log.date).slice(0, 7);
  const d = getLogDate(log);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}` : '';
}
function getUserName(log) {
  return (
    log?.user_name ||
    log?.member_name ||
    log?.username ||
    log?.user?.name ||
    log?.user ||
    '—'
  );
}
function toMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}
function getHours(log) {
  if (log?.hours != null) return Number(log.hours) || 0;
  const ms = toMinutes(log?.start || log?.start_time);
  const me = toMinutes(log?.end || log?.end_time);
  if (ms != null && me != null && me > ms) return Math.round(((me - ms) / 60) * 100) / 100;
  const sa = parseSafeDateAny(log?.started_at || log?.startedAt);
  const ea = parseSafeDateAny(log?.ended_at || log?.endedAt);
  if (sa && ea && ea > sa) return Math.round((((ea - sa) / 1000 / 60 / 60)) * 100) / 100;
  return 0;
}
function getAmount(log) {
  if (log?.amount != null) return Number(log.amount) || 0;
  const h = getHours(log);
  const rate = Number(log?.rate ?? log?.hourly_rate_snapshot ?? log?.hourly_rate ?? 0) || 0;
  return Math.round(h * rate);
}
const fmtCLP = (n) => (n === '' || n == null) ? '' : Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

// Formatear fecha para mostrar (DD-MM-YYYY)
function formatDraftDate(dateStr) {
  if (!dateStr) return '—';
  // Si es ISO string (2025-10-09T03:00:00.000Z)
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    const d = new Date(dateStr);
    if (!isNaN(d)) {
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    }
  }
  // Si es formato YYYY-MM-DD
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-');
    return `${d}-${m}-${y}`;
  }
  return String(dateStr);
}

/* ================ Componente ================ */
export default function WorklogView() {
  const { projectId: routeProjectId } = useParams();
  const { token, user, hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole('admin') : (user?.role === 'admin');
  const canViewAll = isAdmin;
  const myId = user?.id != null ? String(user.id) : '';

  const topRef = useRef(null);
  const [saved, setSaved] = useState(false);

  const todayISO = useMemo(() => fmtDateISO(new Date()), []);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(routeProjectId || '');
  const [project, setProject] = useState(null);
  const [amInProject, setAmInProject] = useState(false);

  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [limit, setLimit] = useState(20);

  // Para PDF centralizado
  const [allProjectLogs, setAllProjectLogs] = useState([]);

  // Modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLog, setViewLog] = useState(null);

  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  });

  // Form
  const now = new Date();
  const defaultStart = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const defaultEnd = `${pad((now.getHours() + 1) % 24)}:${pad(now.getMinutes())}`;

  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  // Miembro seleccionado para el formulario (guarda y calcula tarifa)
  const [formMemberId, setFormMemberId] = useState(isAdmin ? '' : myId);
  // Miembro seleccionado para filtrar "Últimas entradas" (solo admins)
  const [filterMemberId, setFilterMemberId] = useState('');
  const [scopeMineOnly, setScopeMineOnly] = useState(!canViewAll);
  const [includeHtml, setIncludeHtml] = useState(false);
  const [activity, setActivity] = useState('');
  const [note, setNote] = useState('');
  const [billable, setBillable] = useState(false);
  const [error, setError] = useState('');
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogDate, setEditingLogDate] = useState('');
  const [supportsEdit, setSupportsEdit] = useState(false); //PONER EN TRUE CUANDO SE TENGA QUE HABILITAR EL EDITAR
  const [isSaving, setIsSaving] = useState(false);
  // ==== Drafts ====
  const [drafts, setDrafts] = useState([]);
  const [activeDraft, setActiveDraft] = useState(null); // {id, ...payload}
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimerRef = useRef(null);
  // Permisos + edición (helpers dentro para acceso a estado)
  const canEditLog = (l) => {
    const uid = String(l?.user_id ?? l?.user?.id ?? l?.member_id ?? '');
    return isAdmin || (uid && uid === String(myId));
  };

  const handleImportRecords = async (records) => {
    if (!projectId || !formMemberId) {
      toast.error('Selecciona proyecto y miembro antes de importar');
      return;
    }

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    for (const rec of records) {
      try {
        const payload = {
          user_id: Number(formMemberId),
          date: rec.dateISO,
          start: rec.start,
          end: rec.end,
          progress_note: rec.note,
          billable: 0,
          activity_code: null,
        };

        await createProjectLogApi(Number(projectId), payload, token);
        successCount++;
      } catch (err) {
        errorCount++;
        errors.push(`Fila ${rec.rowNum}: ${err?.payload?.error || err.message}`);
      }
    }

    await fetchLogs(projectId, limit);

    if (errorCount === 0) {
      toast.success(`${successCount} registros importados exitosamente`);
    } else {
      toast.error(`Importados: ${successCount} | Errores: ${errorCount}`);
      if (errors.length > 0) {
        console.error('Errores de importación:', errors);
      }
    }

    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  
  function formToDraftPayload() {
    // la fecha para Draft: si estás editando un log, respeta editingLogDate; si no, hoy
    const date = (editingLogId ? (editingLogDate || todayISO) : todayISO);
    return {
      id: activeDraft?.id,              // permite upsert
      title: null,                      // opcional (puedes añadir un input si quieres)
      date,
      start,
      end,
      progress_note: (note || '').trim(),
      hourly_rate_snapshot: Number.isFinite(derivedRate) ? Number(derivedRate) : null,
      billable: billable ? 1 : 0,
      activity_code: activity || null,
      payload_json: null,
    };
  }
  
  function applyDraftToForm(d) {
    // Copia 1:1 lo disponible; respeta tus defaults cuando falte info
    setStart(d?.start || defaultStart);
    setEnd(d?.end || defaultEnd);
    setActivity(d?.activity_code || '');
    setNote(d?.progress_note || '');
    setBillable((d?.billable ?? 1) === 1);
    
    // Cargar el usuario del borrador si es admin, o usar myId si es programmer
    if (isAdmin && d?.user_id) {
      setFormMemberId(String(d.user_id));
    } else if (!isAdmin && myId) {
      setFormMemberId(myId);
    }
    // Si administras fecha editable: setEditingLogDate(d?.date || todayISO);
  }
  
  const beginEdit = (l) => {
    if (!l) return;
    setStart(getStartStr(l) || defaultStart);
    setEnd(getEndStr(l) || defaultEnd);
    setActivity(l?.activity_code || '');
    setNote(getNoteStr(l) || '');
    setBillable(getBillable(l));
    if (isAdmin) {
      const uid = String(l?.user_id ?? l?.user?.id ?? l?.member_id ?? '');
      if (uid) setFormMemberId(uid);
    }
    const d = getLogDate(l);
    if (d) {
      const y = d.getFullYear(), m = pad(d.getMonth() + 1), dd = pad(d.getDate());
      setEditingLogDate(`${y}-${m}-${dd}`);
    } else {
      setEditingLogDate(todayISO);
    }
    setEditingLogId(l?.id ?? null);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const cancelEdit = () => {
    setEditingLogId(null);
    setEditingLogDate('');
    resetFormToNew(); // ← limpia el form y deja miembro acorde al rol
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const resetFormToNew = () => {
    setStart(defaultStart);
    setEnd(defaultEnd);
    setActivity('');
    setNote('');
    setBillable(false);
    if (!isAdmin && myId) setFormMemberId(myId);
  };
  // Proyectos (si NO admin → solo asignados)
  useEffect(() => {
    (async () => {
      try {
        const mine = !canViewAll;
        const ps = await listProjectsApi(token, mine ? { mine: true } : undefined);
        setProjects(ps || []);
        if (!routeProjectId && (!projectId || !String(projectId).trim())) {
          if (ps && ps.length) setProjectId(String(ps[0].id));
        }
      } catch {
        setProjects([]);
      }
    })();
  }, [token, canViewAll]); // routeProjectId y projectId se manejan aparte

  // Proyecto seleccionado
  useEffect(() => {
    const p = (projects || []).find(p => String(p.id) === String(projectId)) || null;
    setProject(p || null);
  }, [projects, projectId]);

  // Miembros y logs
  const fetchMembers = async (pid) => {
    if (!pid) { setMembers([]); return; }
    try {
      const rows = await listProjectMembersApi(pid, token);
      const actives = (rows || []).filter(m => m?.status === 'active' && m?.unassigned_at == null);
      setMembers(actives);
      if (isAdmin) {
        setFormMemberId(prev => prev);
        setAmInProject(true);
      } else if (myId) {
        const am = actives.some(m => String(m.user_id) === String(myId));
        setAmInProject(am);
        setFormMemberId(myId);
      }
    } catch { setMembers([]); }
  };
  const fetchLogs = async (pid, lim = limit) => {
    if (!pid) { setLogs([]); return; }
    try { setLogs(await listProjectLogsApi(pid, token, lim) || []); }
    catch { setLogs([]); }
  };
  useEffect(() => {
    if (projectId) { fetchMembers(projectId); fetchLogs(projectId, limit); }
  }, [projectId]); // eslint-disable-line
  useEffect(() => { if (projectId) fetchLogs(projectId, limit); }, [limit]); // eslint-disable-line

  // Logs para PDF
  useEffect(() => {
    (async () => {
      if (!projectId) { setAllProjectLogs([]); return; }
      try {
        const rows = await listProjectLogsApi(Number(projectId), token, 10000) || [];
        const norm = rows.map(l => ({
          ...l,
          date_obj: (l?.date ? new Date(l.date) : null),
          date_display: (l?.date ? String(l.date).replace('T', ' ').slice(0, 16) : '')
        }));
        setAllProjectLogs(norm);
      } catch {
        setAllProjectLogs([]);
      }
    })();
  }, [projectId, token]);

  // Drafts
  async function reloadDrafts(pid = projectId) {
    if (!pid) { setDrafts([]); return; }
    try {
      const rows = await listProjectDraftsApi(Number(pid), token);
      setDrafts(rows || []);
    } catch {
      setDrafts([]);
    }
  }

  useEffect(() => { reloadDrafts(projectId); }, [projectId, token]);

  // Autosave del borrador activo
  useEffect(() => {
    if (!activeDraft) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(async () => {
      setIsSavingDraft(true);
      try {
        const payload = formToDraftPayload();
        const saved = await upsertDraftApi(Number(projectId), payload, token);
        setActiveDraft(saved || payload);
        setLastSavedAt(new Date());
        upsertDraftInState(saved || payload);
      } finally {
        setIsSavingDraft(false);
      }
    }, 1000);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  // deps: cambios del formulario que deben disparar autosave
  }, [activeDraft, projectId, start, end, note, billable, activity, editingLogDate, token]);

  // Previews
  const hoursPreview = useMemo(() => getHours({ start, end }), [start, end]);
  const derivedRate = useMemo(() => {
    const m = members.find(x => String(x.user_id) === String(formMemberId));
    const memberRate = m?.hourly_rate;
    const projectRate = project?.default_hourly_rate;
    return Number(memberRate ?? projectRate ?? 0) || 0;
  }, [members, formMemberId, project]);
  const amountPreview = useMemo(() => Math.round(hoursPreview * derivedRate), [hoursPreview, derivedRate]);

  // Helpers para actualizar drafts localmente (evita recargar)
  const upsertDraftInState = (updated) => {
    if (!updated) return;
    setDrafts(prev => {
      const list = Array.isArray(prev) ? [...prev] : [];
      const idx = list.findIndex(d => String(d.id) === String(updated.id));
      if (idx >= 0) {
        list[idx] = { ...list[idx], ...updated };
      } else {
        list.unshift(updated);
      }
      return list;
    });
  };

  const removeDraftFromState = (id) => {
    setDrafts(prev => (prev || []).filter(d => String(d.id) !== String(id)));
  };

  // Handlers para Drafts
  async function newDraft() {
    if (!projectId) { setError('Selecciona un proyecto antes de crear un borrador'); return; }
    const payload = formToDraftPayload();
    delete payload.id;
    const created = await upsertDraftApi(Number(projectId), payload, token);
    setActiveDraft(created);
    upsertDraftInState(created);
  }

  async function saveDraftNow() {
    if (!activeDraft) return newDraft();
    setIsSavingDraft(true);
    try {
      const payload = formToDraftPayload();
      const saved = await upsertDraftApi(Number(projectId), payload, token);
      setActiveDraft(saved || payload);
      setLastSavedAt(new Date());
      upsertDraftInState(saved || payload);
    } finally {
      setIsSavingDraft(false);
    }
  }

  function editDraft(d) {
    setActiveDraft(d);
    applyDraftToForm(d);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function copyDraft(d) {
    applyDraftToForm(d);
    setActiveDraft(null); // copiar no obliga a editar el draft
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function publishDraft(d) {
    try {
      await publishDraftApi(Number(d.id), token);
      if (activeDraft?.id === d.id) setActiveDraft(null);
      removeDraftFromState(d.id);
      await fetchLogs(projectId, limit);
      toast.success('Borrador publicado exitosamente');
    } catch (err) {
      toast.error(`Error al publicar: ${err.message}`);
      console.error(err);
    }
  }

  async function deleteDraft(d) {
    if (!window.confirm('¿Estás seguro de eliminar este borrador?')) return;
    try {
      await deleteDraftApi(Number(d.id), token);
      if (activeDraft?.id === d.id) setActiveDraft(null);
      removeDraftFromState(d.id);
      toast.success('Borrador archivado exitosamente');
    } catch (err) {
      toast.error(`Error al eliminar: ${err.message}`);
      console.error(err);
    }
  }
  // Guardar / Actualizar
  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    if (!projectId) { 
      toast.error('Proyecto es obligatorio');
      setIsSaving(false);
      return; 
    }
    const finalMemberId = isAdmin ? formMemberId : myId;
    if (!finalMemberId) { 
      toast.error('Miembro es obligatorio');
      setIsSaving(false);
      return; 
    }
    if (!isAdmin && !amInProject) { 
      toast.error('No puedes guardar en un proyecto al que no estás asignado.');
      setIsSaving(false);
      return; 
    }

    try {
      const payload = {
        user_id: Number(finalMemberId),
        date: editingLogId ? (editingLogDate || todayISO) : todayISO,
        start,
        end,
        progress_note: (note || '').trim(),
        billable: billable ? 1 : 0,
        activity_code: activity || null,
      };

      console.log('Saving payload:', payload);
      if (editingLogId) {
        try {
          await updateProjectLogApiCompat(Number(projectId), Number(editingLogId), payload, token);
          toast.success('Registro actualizado exitosamente');
        } catch (errUpd) {
          if (String(errUpd?.message).includes('NO_UPDATE_ENDPOINT')) {
            setSupportsEdit(false);
            toast.error('Tu API no expone endpoint para editar worklogs. No se aplicaron cambios.');
            setIsSaving(false);
            return;
          }
          throw errUpd;
        }
      } else {
        await createProjectLogApi(Number(projectId), payload, token);
        toast.success('Registro guardado exitosamente');
      }

      if (editingLogId) {
        cancelEdit();
      } else {
        resetFormToNew();
      }
      await fetchLogs(projectId, limit);
      setSaved(false); // Ya no necesitamos el banner verde
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      const msg = String(err?.payload?.error || err?.message || 'Error al guardar');
      toast.error(msg);
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Filtros / vistas
  const monthFilteredLogs = useMemo(() => {
    const src = logs || [];
    if (!month) return src;
    return src.filter(l => getLogMonthKey(l) === month);
  }, [logs, month]);

  const displayedLogs = useMemo(() => {
    let out = monthFilteredLogs;
    if (!canViewAll) {
      if (scopeMineOnly && myId) {
        out = out.filter(l => String(l.user_id ?? l.user?.id ?? l.member_id ?? '') === String(myId));
      }
    } else {
      if (filterMemberId) {
        out = out.filter(l => String(l.user_id ?? l.user?.id ?? l.member_id ?? '') === String(filterMemberId));
      }
    }
    return out;
  }, [monthFilteredLogs, canViewAll, scopeMineOnly, myId, filterMemberId]);
  // Contadores para avisar si hay más registros 
  const mineCount = useMemo(() => monthFilteredLogs.filter(
    l => String(l.user_id ?? l.user?.id ?? l.member_id ?? '') === String(myId)
  ).length, [monthFilteredLogs, myId]);
  const totalCount = monthFilteredLogs.length;

  const summary = useMemo(() => {
    const map = new Map();
    (displayedLogs || []).forEach(l => {
      const k = l.user_id ?? l.user?.id ?? getUserName(l);
      const prev = map.get(k) || { user_name: getUserName(l), hours: 0, amount: 0 };
      prev.hours += getHours(l);
      prev.amount += getAmount(l);
      map.set(k, prev);
    });
    return Array.from(map.values());
  }, [displayedLogs]);

  // Modal
  const openView = (log) => { setViewLog(log); setViewOpen(true); };
  const closeView = () => { setViewOpen(false); setViewLog(null); };

  return (
    <Layout title={`Bitácora ${project ? `– ${project.name}` : ''}`}>
      <Toaster position="top-right" reverseOrder={false} />
      <div ref={topRef} />
      {saved && (
        <div className="card success-banner">
          <b>¡Guardado!</b> La entrada de bitácora se registró correctamente.
        </div>
      )}

      {/* Indicador de modo borrador activo */}
      {activeDraft && (
        <div className="draft-mode-banner">
          <div className="draft-mode-banner-content">
            <span className="draft-mode-icon">✏️</span>
            <div className="draft-mode-text">
              <div className="draft-mode-title">Modo Borrador Activo</div>
              <div className="draft-mode-subtitle">
                Editando borrador #{activeDraft.id} • Guardado automático activado
              </div>
            </div>
          </div>
          <button 
            className="draft-mode-close" 
            onClick={() => {
              setActiveDraft(null);
              resetFormToNew();
            }}
            title="Salir del modo borrador"
          >
            Cerrar borrador
          </button>
        </div>
      )}

      {/* Header */}
      <div className="worklog-header-actions">
        <div>
          <Link className="btn" to="/bitacora">Regresar a Bitácora</Link>
        </div>
        <div className="worklog-export-buttons">
          <ImportWorklogsExcel
            projectId={projectId}
            memberId={formMemberId}
            onImport={handleImportRecords}
            render={(onClick) => (
              <button
                className="btn secondary"
                onClick={onClick}
                disabled={!projectId || !formMemberId}
                title="Importar registros desde Excel"
              >
                <span className="btn-with-icon">
                  <FaFileUpload /> Importar Excel
                </span>
              </button>
            )}
          />
          <ExportWorklogsExcel
            logs={displayedLogs}
            filename={`worklog_p${projectId}_${month}_completo`}
            getUserName={getUserName}
            getHours={getHours}
            getAmount={getAmount}
            render={(onExport) => (
              <button
                className="btn"
                onClick={onExport}
                disabled={!projectId}
                title="Exportar a Excel (Detalle + Resumen) con datos filtrados"
              >
                <span className="btn-with-icon">
                  <FaFileExcel /> Excel Completo
                </span>
              </button>
            )}
          />

          {/* PDF */}
          <ExportProjectPDF
            project={project}
            logs={allProjectLogs}
            monthLabel={month}
            buttonProps={{
              className: 'btn',
              disabled: !projectId,
              title: 'Descargar PDF del proyecto (todas las entradas de todos los programadores)',
            }}
          />
        </div>
      </div>


      {/* Proyecto */}
      <div className="card worklog-card-spacing">
        <FormRow label="Proyecto">
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="worklog-select-full" disabled={!!routeProjectId}>
            <option value="">— Selecciona proyecto —</option>
            {projects.map(p => (<option key={p.id} value={p.id}>{p.name} — {p.client_name}</option>))}
          </select>
        </FormRow>
      </div>

      {/* Registrar horas */}
      <div className="card worklog-card-spacing-sm">
        <h4 className="worklog-section-title">Registrar horas</h4>
        {error && (
          <div className="card error-banner">
            {error}
          </div>
        )}
        {!isAdmin && projectId && !amInProject && (
          <div className="card warning-banner">
            No perteneces a este proyecto. Selecciona uno de tus proyectos asignados en el selector de "Proyecto".
          </div>
        )}

        {/* Fecha */}
        <div className="worklog-date-display">
          <span className="worklog-date-label">
            {editingLogId ? 'Fecha (del registro)' : 'Fecha (fija)'}
          </span>
          <div className="worklog-date-badge">{editingLogId ? (editingLogDate || todayISO) : todayISO}</div>
        </div>

        {/* Fila 1 */}
        <div className="worklog-form-grid-2">
          <FormRow label="Miembro">
            <select value={formMemberId} onChange={(e) => setFormMemberId(e.target.value)} className="worklog-select-full" disabled={!isAdmin || !amInProject}>
              <option value="">— Selecciona —</option>
              {members.map(m => (<option key={m.user_id} value={m.user_id}>{m.user_name}</option>))}
            </select>
          </FormRow>
          <FormRow label="Etiqueta / Actividad">
            <select value={activity} onChange={(e) => setActivity(e.target.value)} className="worklog-select-full">
              <option value="">— Selecciona actividad —</option>
              <option value="DEV">DEV</option>
              <option value="Terreno">Terreno</option>
              <option value="Reunión">Reunión</option>
              <option value="Llamada">Llamada</option>
              <option value="Capacitación">Capacitación</option>
              <option value="Asistencia remota">Asistencia remota</option>
            </select>
          </FormRow>
        </div>

        {/* Fila 2 */}
        <div className="worklog-form-grid-equal">
          <FormRow label="Inicio">
            <input type="time" value={start} onChange={(e) => setStart(e.target.value)} step="60" className="worklog-time-input" />
          </FormRow>
          <FormRow label="Término">
            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} step="60" className="worklog-time-input" />
          </FormRow>
        </div>

        {/* Píldoras */}
        <div className="worklog-pills-container">
          <div className="pill-base pill-info">Tarifa automática: <span className="pill-value">{fmtCLP(derivedRate)}</span></div>
          <div className="pill-base pill-plain">Horas: <span className="pill-value">{hoursPreview.toFixed(2)} h</span></div>
          <div className="pill-base pill-success">Monto estimado: <span className="pill-value">{fmtCLP(amountPreview)}</span></div>
        </div>

        {/* Nota */}
        <FormRow label="Avance (nota)">
          <RichTextEditor value={note} onChange={setNote} placeholder="Describe el avance realizado…" />
        </FormRow>

        {/* Facturable + Guardar */}
        <div className="worklog-form-actions">
          {/* Campo billable oculto - siempre false */}
          {/* <label className="worklog-billable-checkbox">
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
            Facturable
          </label> */}
          <div className="worklog-action-buttons" style={{ width: '100%', justifyContent: 'flex-end' }}>
            {editingLogId && (
              <button 
                className="btn secondary" 
                type="button" 
                onClick={cancelEdit}
                disabled={isSaving}
              >
                Cancelar edición
              </button>
            )}
            <button 
              className="btn" 
              onClick={handleSave}
              disabled={isSaving || !projectId || (!isAdmin && !myId) || (isAdmin && !formMemberId)}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner"></span>
                  {editingLogId ? 'Actualizando...' : 'Guardando...'}
                </>
              ) : (
                editingLogId ? 'Actualizar' : 'Guardar'
              )}
            </button>
            {!activeDraft && (
              <button 
                className="btn" 
                type="button" 
                onClick={newDraft} 
                title="Crear un borrador desde el formulario actual"
                disabled={isSaving}
              >
                Nuevo borrador
              </button>
            )}
            {activeDraft && (
              <div className="draft-autosave-indicator">
                {isSavingDraft ? (
                  <>
                    <div className="draft-autosave-spinner"></div>
                    <span>Guardando...</span>
                  </>
                ) : lastSavedAt ? (
                  <>
                    <span className="draft-autosave-icon">✓</span>
                    <span>Guardado {new Date(lastSavedAt).toLocaleTimeString('es-CL', {hour: '2-digit', minute: '2-digit'})}</span>
                  </>
                ) : (
                  <span>Borrador activo</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Borradores */}
      <div className="card worklog-card-spacing">
        <div className="worklog-entries-header">
          <div>
            <h4 className="worklog-entries-title">Borradores</h4>
            <div className="worklog-entries-subtitle">
              {drafts?.length ? `${drafts.length} borrador(es) sin publicar` : 'No tienes borradores en este proyecto.'}
            </div>
          </div>
          <div className="worklog-filters">
            <button className="btn" onClick={() => reloadDrafts()} disabled={!projectId}>Recargar</button>
          </div>
        </div>

        {drafts?.length > 0 ? (
          <table className="worklog-table drafts-table">
            <thead>
              <tr>
                <th>Fecha</th>
                {isAdmin && <th>Programador</th>}
                <th>Resumen</th>
                <th className="align-right draft-hours-column">Horas (prev)</th>
                <th className="align-center col-narrow">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map(d => {
                // preview simple de horas estimadas (si hay rango)
                const hrs = (d?.start && d?.end) ? getHours({ start: d.start, end: d.end }) : 0;
                const isActive = activeDraft?.id === d.id;
                const isMine = String(d?.user_id) === String(myId);
                return (
                  <tr key={d.id} className={isActive ? 'draft-row' : ''}>
                    <td>
                      <div className="draft-date">{formatDraftDate(d?.date)}</div>
                      {isActive && (
                        <div className="draft-badge" style={{marginTop: 4}}>
                          <span>✏️</span> Editando
                        </div>
                      )}
                      {!isMine && isAdmin && (
                        <div className="draft-badge" style={{marginTop: 4, background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', color: '#1e40af', borderColor: '#93c5fd'}}>
                          <span>👤</span> Otro usuario
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td>
                        <div style={{fontWeight: 500}}>
                          {d?.user_name || 'Usuario desconocido'}
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="draft-preview">
                        <div className="draft-title">{d?.title || '(sin título)'}</div>
                        <div className="draft-note-preview" dangerouslySetInnerHTML={{__html: (d?.progress_note || '').slice(0,140) }} />
                      </div>
                    </td>
                    <td className="align-right draft-hours-column">
                      <span className="draft-hours-preview">{hrs.toFixed(2)}</span>
                    </td>
                    <td className="align-center">
                      <div className="draft-actions">
                        <button 
                          className="draft-btn" 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyDraft(d); }} 
                          title="Copiar contenido al formulario (sin vincular al borrador, para crear un nuevo registro)"
                        >
                          📋 Copiar
                        </button>
                        <button 
                          className="draft-btn draft-btn-edit" 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); editDraft(d); }} 
                          title="Editar este borrador (activa guardado automático, vincula formulario con el borrador)"
                        >
                          ✏️ Editar
                        </button>
                        <button 
                          className="draft-btn draft-btn-publish" 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); publishDraft(d); }} 
                          title="Publicar como worklog definitivo (crea el registro y archiva el borrador)"
                        >
                          ✅ Publicar
                        </button>
                        <button 
                          className="draft-btn draft-btn-delete" 
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteDraft(d); }} 
                          title="Archivar este borrador (oculta pero no elimina de la base de datos)"
                        >
                          🗑️ Archivar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="drafts-empty-state">
            <div className="drafts-empty-icon">📝</div>
            <div className="drafts-empty-text">No hay borradores guardados</div>
          </div>
        )}
      </div>

      {/* Últimas entradas */}
      <div className="card worklog-card-spacing">
        <div className="worklog-entries-header">
          <div>
            <h4 className="worklog-entries-title">Últimas entradas</h4>
            {!canViewAll && (
              <div className="worklog-entries-subtitle">
                {scopeMineOnly
                  ? `Viendo ${mineCount} de ${totalCount} entradas del mes (desmarca para ver todas).`
                  : `Viendo todas las ${totalCount} entradas del mes.`}
              </div>
            )}
          </div>
          <div className="worklog-filters">
            <label className="worklog-filter-label">Mes:&nbsp;<input type="month" value={month} onChange={(e) => setMonth(e.target.value)} /></label>
            <label className="worklog-filter-label">Límite:&nbsp;
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                {[10, 20, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            {canViewAll && (
              <label className="worklog-filter-label">Miembro:&nbsp;
                <select value={filterMemberId} onChange={(e) => setFilterMemberId(e.target.value)}>
                  <option value="">(Todos)</option>
                  {(members || []).map(m => <option key={m.user_id} value={m.user_id}>{m.user_name}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>

<table className="worklog-table">
  <thead>
    <tr>
      <th>Fecha</th>
      <th>Programador</th>
      <th>Avance</th>
      <th className="align-right">Horas</th>
      <th className="align-right">Monto</th>
      {/* <th className="align-center col-narrow">Editar</th> */}
      {/* PENDIENTE DE HABILITAR EL EDITAR UN AVANCE */}
    </tr>
  </thead>
  <tbody>
    {displayedLogs.map(l => {
      const dt = getLogDate(l);
      const userName = getUserName(l);
      const hrs = getHours(l);
      const amt = getAmount(l);
      const startTime = getStartStr(l);
      const endTime = getEndStr(l);
      return (
        <tr key={l.id ?? `${l.date}-${l.start}-${userName}`}>
          <td>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div className="text-sm font-medium text-slate-900">
                {dt ? formatCL(dt).split(' ')[0] : (l?.date ? String(l.date).slice(0, 10) : '—')}
              </div>
              <div className="text-xs text-slate-500" style={{ fontWeight: 500 }}>
                {startTime && endTime ? `${startTime} - ${endTime}` : (startTime || endTime || '—')}
              </div>
            </div>
          </td>
          <td>{userName}</td>
          <td>
            <button className="btn" onClick={() => openView(l)}>Ver avance</button>
          </td>
          <td className="align-right">{hrs.toFixed(2)}</td>
          <td className="align-right">{fmtCLP(amt)}</td>
        </tr>
      );
    })}
    {!displayedLogs.length && (
      <tr><td colSpan={5} className="empty-message">Sin datos para los filtros.</td></tr>
    )}
  </tbody>
</table>

        {/* Resumen mensual */}
        <div className="worklog-summary">
          <h4 className="worklog-summary-title">Resumen mensual</h4>
          <table className="worklog-table">
            <thead>
              <tr>
                <th>Programador</th>
                <th className="align-right">Horas</th>
                <th className="align-right">Monto</th>
              </tr>
            </thead>
            <tbody>
              {summary.map(s => (
                <tr key={s.user_name}>
                  <td>{s.user_name}</td>
                  <td className="align-right">{s.hours.toFixed(2)}</td>
                  <td className="align-right">{fmtCLP(s.amount)}</td>
                </tr>
              ))}
              {!summary.length && (
                <tr><td colSpan={3} className="empty-message">Sin datos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Modal: Ver Avance ===== */}
      {viewOpen && (
        <ModalShell open={viewOpen} onClose={closeView} title="Avance de bitácora">
          <div className="worklog-modal-grid">
            <div className="worklog-modal-field">
              <div className="worklog-modal-field-label">Fecha</div>
              {(() => {
                const dt = getLogDate(viewLog);
                return dt ? formatCL(dt) : (viewLog?.date ? `${viewLog.date} ${viewLog?.start || ''}`.trim() : '—');
              })()}
            </div>
            <div className="worklog-modal-field">
              <div className="worklog-modal-field-label">Programador</div>
              {getUserName(viewLog)}
            </div>
            <div className="worklog-modal-field">
              <div className="worklog-modal-field-label">Horas</div>
              {getHours(viewLog).toFixed(2)}
            </div>
            <div className="worklog-modal-field">
              <div className="worklog-modal-field-label">Monto</div>
              {fmtCLP(getAmount(viewLog))}
            </div>
            {viewLog?.activity_code && (
              <div className="worklog-modal-field">
                <div className="worklog-modal-field-label">Etiqueta</div>
                {viewLog.activity_code}
              </div>
            )}
          </div>

          <div
            className="worklog-modal-content"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewLog?.progress_note || viewLog?.note || '') }}
          />

          <div className="worklog-modal-actions">
            <div>
              <ExportSingleLogPDF
                log={viewLog}
                project={project}
                render={(onExport) => (
                  <button
                    className="btn secondary"
                    onClick={onExport}
                    title="Descargar este avance como PDF"
                  >
                    <span className="btn-with-icon">
                      <FaFilePdf /> Descargar PDF
                    </span>
                  </button>
                )}
              />
            </div>
            <div className="worklog-modal-buttons">
              <button className="btn" onClick={closeView}>Cerrar</button>
            </div>
          </div>

        </ModalShell>
      )}
    </Layout>
  );
}
